import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Mail, X, ChevronLeft, ChevronRight } from 'lucide-react';

// Showroom images
const showroomImages = [
  { src: '/showroom/BCD_3723.jpg', alt: 'LV1 Showroom Interior' },
  { src: '/showroom/BCD_3725.jpg', alt: 'LV1 Showroom Display' },
  { src: '/showroom/BCD_3736.jpg', alt: 'LV1 Showroom Space' },
];

// Scroll reveal hook
const useScrollReveal = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, ...options }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onPrev, onNext }) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onPrev, onNext]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 200);
  };

  const [touchStart, setTouchStart] = useState(null);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      if (diff > 0) onNext();
      else onPrev();
    }
    setTouchStart(null);
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-all duration-200 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/95" />

      <button
        onClick={handleClose}
        className="absolute top-6 right-6 z-10 p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
      >
        <X size={24} className="text-white" />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 md:left-8 z-10 p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
      >
        <ChevronLeft size={28} className="text-white" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 md:right-8 z-10 p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
      >
        <ChevronRight size={28} className="text-white" />
      </button>

      <div
        className={`relative max-w-[90vw] max-h-[85vh] transition-transform duration-200 ${
          isClosing ? 'scale-95' : 'scale-100'
        }`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={images[currentIndex].src}
          alt={images[currentIndex].alt}
          className="max-w-full max-h-[85vh] w-auto h-auto rounded-lg"
          style={{
            boxShadow: '0 25px 80px rgba(0,0,0,0.5)'
          }}
        />
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3">
        {images.map((img, idx) => (
          <button
            key={idx}
            onClick={(e) => {
              e.stopPropagation();
              const diff = idx - currentIndex;
              if (diff > 0) for (let i = 0; i < diff; i++) onNext();
              else for (let i = 0; i < Math.abs(diff); i++) onPrev();
            }}
            className={`w-16 h-12 overflow-hidden rounded-md transition-all ${
              idx === currentIndex
                ? 'ring-2 ring-white opacity-100'
                : 'opacity-40 hover:opacity-70'
            }`}
          >
            <img
              src={img.src}
              alt={img.alt}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>

      <div className="absolute top-6 left-6 text-white/50 text-sm font-light">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
};

// Gallery Image Component - clean floating style
const GalleryImage = ({ src, alt, onClick, delay = 0 }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [ref, isVisible] = useScrollReveal();

  return (
    <div
      ref={ref}
      className="relative overflow-hidden cursor-pointer"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        borderRadius: '12px',
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? (isHovered ? 'translateY(-4px)' : 'translateY(0)')
          : 'translateY(20px)',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.4s ease`,
        boxShadow: isHovered
          ? '0 24px 48px rgba(0,0,0,0.5)'
          : '0 12px 32px rgba(0,0,0,0.3)',
      }}
    >
      {/* Loading placeholder */}
      {!isLoaded && (
        <div className="w-full bg-white/5 animate-pulse" style={{ aspectRatio: '16/10' }} />
      )}

      <img
        src={src}
        alt={alt}
        className="w-full"
        style={{
          display: isLoaded ? 'block' : 'none',
          borderRadius: '12px',
          transform: isHovered ? 'scale(1.02)' : 'scale(1)',
          transition: 'transform 0.5s ease',
        }}
        onLoad={() => setIsLoaded(true)}
      />
    </div>
  );
};

const ShowroomPage = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [headerRef, headerVisible] = useScrollReveal();
  const [infoRef, infoVisible] = useScrollReveal();

  // Preload all images immediately when component mounts
  useEffect(() => {
    showroomImages.forEach((image) => {
      const img = new Image();
      img.src = image.src;
    });
  }, []);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const nextImage = useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % showroomImages.length);
  }, []);

  const prevImage = useCallback(() => {
    setLightboxIndex((prev) => (prev - 1 + showroomImages.length) % showroomImages.length);
  }, []);

  return (
    <main className="bg-[#0a0a0a] min-h-screen pt-32 pb-24 px-6">
      <div className="max-w-[1320px] mx-auto">
        {/* Header */}
        <div
          ref={headerRef}
          className="text-center mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <p className="text-[#6a6a6a] text-[12px] mb-3 uppercase tracking-[0.15em]">Our Space</p>
          <h1 className="text-[44px] md:text-[56px] font-extralight text-white mb-5 tracking-[-0.02em]">
            LV1 Showroom
          </h1>
          <p className="text-[#7a7a7a] text-[16px] leading-[1.6] max-w-[520px] mx-auto">
            Where brands become assortments. Built for hands-on review and line building with retail partners.
          </p>
        </div>

        {/* Hero Image */}
        <div className="mb-6">
          <GalleryImage
            src={showroomImages[0].src}
            alt={showroomImages[0].alt}
            onClick={() => openLightbox(0)}
            delay={0}
          />
        </div>

        {/* Two smaller images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
          <GalleryImage
            src={showroomImages[1].src}
            alt={showroomImages[1].alt}
            onClick={() => openLightbox(1)}
            delay={100}
          />
          <GalleryImage
            src={showroomImages[2].src}
            alt={showroomImages[2].alt}
            onClick={() => openLightbox(2)}
            delay={150}
          />
        </div>

        {/* Info Section */}
        <div
          ref={infoRef}
          className="max-w-[900px] mx-auto"
          style={{
            opacity: infoVisible ? 1 : 0,
            transform: infoVisible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
          }}
        >
          <div
            className="p-10 md:p-14"
            style={{
              background: 'linear-gradient(180deg, rgba(20,20,20,0.8) 0%, rgba(15,15,15,0.6) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '16px',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
              {/* Left: Visit info */}
              <div>
                <h2 className="text-white text-[26px] font-light mb-4">Schedule a Visit</h2>
                <p className="text-[#7a7a7a] text-[15px] leading-[1.7] mb-8">
                  Review current lines, explore upcoming collections, and collaborate on assortment planning.
                </p>

                <Link
                  to="/contact"
                  className="inline-flex items-center px-7 py-3.5 bg-white text-[#0a0a0a] text-[14px] font-medium hover:bg-white/90 transition-all duration-300"
                  style={{ borderRadius: '6px' }}
                >
                  Request Appointment
                </Link>
              </div>

              {/* Right: Contact details */}
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/[0.04] flex items-center justify-center flex-shrink-0 rounded-lg">
                    <MapPin size={18} className="text-white/50" />
                  </div>
                  <div>
                    <p className="text-white text-[14px] font-medium mb-0.5">Location</p>
                    <p className="text-[#6a6a6a] text-[13px]">140 Smith St., Keasbey, NJ 08832</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/[0.04] flex items-center justify-center flex-shrink-0 rounded-lg">
                    <Clock size={18} className="text-white/50" />
                  </div>
                  <div>
                    <p className="text-white text-[14px] font-medium mb-0.5">Hours</p>
                    <p className="text-[#6a6a6a] text-[13px]">By appointment only</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/[0.04] flex items-center justify-center flex-shrink-0 rounded-lg">
                    <Mail size={18} className="text-white/50" />
                  </div>
                  <div>
                    <p className="text-white text-[14px] font-medium mb-0.5">Contact</p>
                    <p className="text-[#6a6a6a] text-[13px]">(732) 738-6800</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={showroomImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </main>
  );
};

export default ShowroomPage;
